import { ArrowBackIcon, ButtonV2 } from '@medeloop/medeloop-components-library';
import { motion, Variants } from 'motion/react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCohortsByDataSourceId } from 'src/api/cohorts-queries';
import {
  OrganizationsResponse,
  OrganizationUnitsResponse,
} from 'src/api/data-sources-dto';
import {
  useGetOrganizationsByDataSourceId,
  useGetOrganizationUnitsByCohortId,
  useGetOrganizationUnitsByDataSourceId,
} from 'src/api/data-sources-queries';
import { TitleSubTitle } from 'src/components';
import CumulativeChart from 'src/components/charts/cumulative-chart';
import DistributionChart from 'src/components/charts/distribution-chart';
import MapChart from 'src/components/charts/map-chart';
import StatCard from 'src/components/charts/stat-card';
import { TimelineInfo } from 'src/components/dataset/data-wiki/timeline-info';
import { useModal } from 'src/hooks/modal-provider';
import { DataSetWiki } from 'src/types/cohorts';
import {
  capitalizeFirstLetter,
  findDemographic,
  formatNumberToKMB,
  formatWikiDataForChart,
  getTotalCount,
} from 'src/utils/datasets';
import { AssignCohortModal } from './assign-cohort-modal';
import { useSelector } from 'react-redux';
import { selectIsUserAdmin } from 'src/store/auth';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: 'beforeChildren',
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 10 },
  },
};
type OrganizationUnitItem = { name: string; organizationId: string };

interface CohortData {
  name?: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  dataWiki?: DataSetWiki;
}

export function CohortDetailsPage() {
  const { dataSourceId, cohortId } = useParams();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const isUserAdmin = useSelector(selectIsUserAdmin);

  const { data: organizationUnitAssignedToCohort } = useGetOrganizationUnitsByCohortId({
    dataSourceId: dataSourceId ?? '',
    cohortId: cohortId ?? '',
  });

  const { data: organizationUnitsData } = useGetOrganizationUnitsByDataSourceId<
    OrganizationUnitItem[]
  >(
    {
      dataSourceId,
    },
    {
      select: (response: OrganizationUnitsResponse): OrganizationUnitItem[] =>
        response?.data?.organizationUnits.map((organizationUnit) => ({
          name: organizationUnit.name,
          organizationId: organizationUnit.organizationId,
        })),
    },
  );

  const { data: organizationsData } = useGetOrganizationsByDataSourceId<OrganizationUnitItem[]>(
    {
      dataSourceId,
    },
    {
      select: (response: OrganizationsResponse): OrganizationUnitItem[] =>
        response?.organizations.map((organization) => ({
          name: organization.organizationDisplayName,
          organizationId: organization.organizationId,
        })),
    },
  );

  const selectableOrganizations = useMemo<OrganizationUnitItem[]>(() => {
    if (!organizationsData || !organizationUnitsData) return [];

    // Combine organizations and organization units, ensuring no duplicates
    const combined = [...organizationsData, ...organizationUnitsData];
    return Array.from(new Set(combined.map((item) => item.organizationId)))
      .map((id) => combined.find((item) => item.organizationId === id))
      .filter((item): item is OrganizationUnitItem => item !== undefined);
  }, [organizationsData, organizationUnitsData]);

  const { data: apiData, isLoading } = useGetCohortsByDataSourceId(dataSourceId ?? '');

  const filteredCohort = useMemo(() => {
    if (!apiData?.data?.cohorts) return null;
    return apiData.data.cohorts.find((cohort) => cohort.cohortId === cohortId);
  }, [apiData, cohortId]);

  const data = filteredCohort as unknown as CohortData;
  const dataSourceWiki: DataSetWiki | undefined = data?.dataWiki;

  const recordCount = dataSourceWiki?.metadata?.record_count || 0;
  const cumulativePatientYears = dataSourceWiki?.metadata?.demographics?.find(
    (demographic) => demographic.metric === 'patient_years',
  );
  const cumulativePatientYearsDistribution = Object.entries(
    cumulativePatientYears?.distribution || {},
  )
    .sort(([periodA], [periodB]) => {
      const yearsA = parseInt(periodA, 10);
      const yearsB = parseInt(periodB, 10);
      return yearsA - yearsB;
    })
    .map(([period, value]) => ({ period, value }));

  const totalRecords =
    recordCount ??
    formatNumberToKMB(
      dataSourceWiki?.metadata?.tables
        .map((x) => x.record_count)
        .reduce((acc, curr) => acc + curr, 0) ?? 0,
    );

  const meanYears = formatNumberToKMB(
    dataSourceWiki?.metadata?.demographics?.find((demo) => demo.metric === 'patient_years')
      ?.mean_patient_years ?? 0,
  );

  const cumulativeYears = formatNumberToKMB(
    dataSourceWiki?.metadata?.demographics?.find((demo) => demo.metric === 'patient_years')
      ?.cumulative_patient_years ?? 0,
  );

  useEffect(() => {
    return () => {
      closeModal();
    };
  }, []);

  const openAssignCohortModal = () => {
    openModal(AssignCohortModal, {
      cohortId: cohortId,
      dataSourceId: dataSourceId,
      onClose: () => closeModal(),
      organizationUnitOptions: selectableOrganizations?.map((ou) => ({
        label: ou.name,
        value: ou.organizationId,
      })),
      selectedValues: organizationUnitAssignedToCohort?.data.organizationUnits.map(
        (ou: { organizationUnitId: string }) => ou.organizationUnitId,
      ),
    });
  };

  return (
    <section className="flex flex-col space-y-4">
      <div className="bg-dark-subdued-background border-b border-stroke-on-dark-subdued py-4">
        <div className="max-w-right-section mx-auto px-6 xl:px-0">
          <button
            className="text-content-on-dark-default text-sm flex gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon className="h-5 w-5" />
            Cohorts
          </button>
          <div className="flex items-center justify-between mt-4 mb-6">
            <TitleSubTitle
              title={data?.name || 'Cohort Details'}
              subTitle={data?.description || 'Loading cohort information...'}
              isLoading={isLoading}
            />
            {isUserAdmin ? (
              <ButtonV2 onClick={openAssignCohortModal}>Assign Cohort</ButtonV2>
            ) : null}
          </div>
          <div>
            <TimelineInfo data={data} />
          </div>
        </div>
      </div>
      <motion.div
        className="w-full max-w-right-section mx-auto px-6 xl:px-0 grid grid-cols-3 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {[
          { title: 'Record Count', value: totalRecords },
          { title: 'Mean Patient Years', value: meanYears },
          { title: 'Cumulative Patient Years', value: cumulativeYears },
        ].map((stat) => (
          <motion.div key={stat.title} variants={itemVariants}>
            <StatCard title={stat.title} value={stat.value} />
          </motion.div>
        ))}
      </motion.div>

      <div className="max-w-right-section w-full  mx-auto px-6 xl:px-0 space-y-4">
        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <motion.div
            variants={itemVariants}
            className="bg-[#0c101b] rounded-lg pb-8 border border-stroke-on-dark-subdued"
          >
            <CumulativeChart
              title="Cumulative Subjects"
              xAxisName="Time period"
              yAxisName="Subjects (n)"
              data={cumulativePatientYearsDistribution}
              startColor="#50D6DE"
              endColor="#50D6DE"
              useKMBFormat={true}
            />
          </motion.div>

          {(() => {
            const geographyData = formatWikiDataForChart(
              dataSourceWiki,
              'geography',
              true, // isCategorical
              true, // excludeUnknown
            ).map(({ label, value }) => ({ state: `${label}`, count: value }));

            const totalCount = getTotalCount(dataSourceWiki, 'geography');

            if (geographyData.length === 0) {
              return null;
            }

            return (
              <motion.div
                variants={itemVariants}
                className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued p-4"
              >
                <h2 className="font-semibold text-gray-100">Location</h2>
                <div className="w-full flex justify-center">
                  <MapChart totalCount={totalCount} data={geographyData} />
                </div>
              </motion.div>
            );
          })()}

          {data?.dataWiki?.metadata?.demographics &&
            findDemographic(data.dataWiki as DataSetWiki, 'age') && (
              <motion.div
                variants={itemVariants}
                className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued"
              >
                <DistributionChart
                  title="Age Distribution"
                  xAxisName="Age (years)"
                  yAxisName="Subjects (n)"
                  data={formatWikiDataForChart(data.dataWiki as DataSetWiki, 'age', false, true)}
                  barsTouching={true}
                  isCategorical={false}
                  binSize={10}
                  bottomMargin={0}
                  startColor="#67E8F9"
                  endColor="#164E63"
                  useKMBFormat={true}
                  totalCount={getTotalCount(data.dataWiki as DataSetWiki, 'age')}
                />
              </motion.div>
            )}

          <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4">
            <div className="bg-[#0c101b] rounded-lg border border-stroke-on-dark-subdued">
              <DistributionChart
                title="Gender Distribution"
                xAxisName="Gender"
                yAxisName="Subjects (n)"
                data={formatWikiDataForChart(dataSourceWiki, 'gender', true, true)}
                totalCount={getTotalCount(dataSourceWiki, 'gender')}
                barsTouching={false}
                isCategorical={true}
                startColor="#67E8F9"
                endColor="#164E63"
                showXAxisName={false}
                sortBars={true}
                useKMBFormat={true}
              />
            </div>
            <div className="bg-[#0c101b] rounded-lg border border-stroke-on-dark-subdued">
              <DistributionChart
                title="Race/Ethnicity Distribution"
                xAxisName="Race/Ethnicity"
                yAxisName="Subjects (n)"
                data={formatWikiDataForChart(dataSourceWiki, 'race', true, true)}
                totalCount={getTotalCount(dataSourceWiki, 'race')}
                barsTouching={false}
                isCategorical={true}
                startColor="#67E8F9"
                endColor="#164E63"
                showXAxisName={false}
                sortBars={true}
                useKMBFormat={true}
              />
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4">
            {dataSourceWiki?.metadata?.highlights?.map((highlight, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-[#0c101b] rounded-lg border border-stroke-on-dark-subdued"
              >
                <DistributionChart
                  title={highlight.metric
                    .split('_')
                    .map((word) => capitalizeFirstLetter(word))
                    .join(' ')}
                  xAxisName={highlight.metric}
                  yAxisName="Subjects (n)"
                  data={Object.entries(highlight.distribution || {})
                    .filter(([key]) => key !== 'null')
                    .map(([key, value]) => ({ label: key, value }))
                    .slice(0, 5)}
                  barsTouching={false}
                  isCategorical={true}
                  startColor="#67E8F9"
                  endColor="#164E63"
                  showXAxisName={false}
                  sortBars={true}
                  useKMBFormat={true}
                  bottomMargin={50}
                  rotateXAxisTicks={true}
                />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
